import React, { useMemo } from "react";
import { Box, Typography, Tooltip, Grid, Button } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InteractiveGauge from "../../shared/Gauges/InteractiveGauge";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatCurrency } from "../../utils/namesFormater";
import { PreferenceDistributionData } from "../../interfaces/DTO/contactPreferences";

import SavingsSummary from "./SavingsSummary";
import { useExposureCalculation } from "../../hooks/useExposureCalculation";

interface Props {
  data: MimshakData[];
  originalData?: MimshakData[];
  onOpenYieldLab?: () => void;
  preferenceData?: PreferenceDistributionData;
  onPreferenceChange?: (data: PreferenceDistributionData) => void;
  title?: string;
}


const ExposureGaugesByMimshakDataInteractive: React.FC<Props> = ({
  data,
  originalData,
  onOpenYieldLab,
  preferenceData,
  onPreferenceChange,
  title,
}) => {

  // Handlers לשינויים בבלוקים
  const handleStockExposureChange = (selectedBlocks: number[]) => {
    if (onPreferenceChange && preferenceData) {
      onPreferenceChange({
        ...preferenceData,
        stockExposure: selectedBlocks
      });
    }
  };

  const handleCurrencyExposureChange = (selectedBlocks: number[]) => {
    if (onPreferenceChange && preferenceData) {
      onPreferenceChange({
        ...preferenceData,
        currencyExposure: selectedBlocks
      });
    }
  };

  const handleForeignExposureChange = (selectedBlocks: number[]) => {
    if (onPreferenceChange && preferenceData) {
      onPreferenceChange({
        ...preferenceData,
        foreignExposure: selectedBlocks
      });
    }
  };

  const { stockExposure, currencyExposure, foreignExposure, totalAmount, totalExcludedAmount, excludedPaths } =
    useExposureCalculation(data, { isModified: !!originalData });



  const buildExcludedTooltip = (): React.ReactNode => {
if (excludedPaths.length === 0) return null;
    const iraItems = excludedPaths.filter(e => e.reason === "IRA");
    const missingItems = excludedPaths.filter(e => e.reason === "missing_data");
    const lines: string[] = [`סכום כולל מוחרג: ${formatCurrency(totalExcludedAmount)}\n`];
    if (iraItems.length > 0) {
      lines.push("מסלולי IRA (בניהול אישי) - מוחרגים מהחישוב:");
      iraItems.forEach(e => lines.push(`  • ${e.fundName ?? "לא ידוע"}${e.policyNumber ? ` (פוליסה ${e.policyNumber})` : ""} — ${formatCurrency(e.amount)}`));
    }
    if (missingItems.length > 0) {
      if (iraItems.length > 0) lines.push("");
      lines.push("מסלולים ללא נתוני חשיפה:");
      missingItems.forEach(e => lines.push(`  • ${e.fundName ?? "לא ידוע"}${e.policyNumber ? ` (פוליסה ${e.policyNumber})` : ""} — ${formatCurrency(e.amount)}`));
    }
    return <span style={{ whiteSpace: "pre-line" }}>{lines.join("\n")}</span>;
  };

  const renderGauge = (
    id: string,
    exposure: { average: number; amount: number },
    label: string,
    initialSelectedBlocks?: number[],
    onSelectedBlocksChange?: (blocks: number[]) => void,
    maxBlocks?: number,
  ) => (
    <Box textAlign="center" p={0} m={0}>
      <InteractiveGauge
        id={id}
        readonly={!onPreferenceChange}
        percent={parseFloat(exposure.average.toFixed(3))}
        label={label}
        amount={formatCurrency(exposure.amount)}
        initialSelectedBlocks={initialSelectedBlocks}
        onSelectedBlocksChange={onSelectedBlocksChange}
        maxBlocks={maxBlocks}
      />
    </Box>
  );

  const allPolicies = useMemo(
    () => data.flatMap((row) => row.data?.products?.[0]?.policies_or_accounts ?? []),
    [data]
  );

  return (
    <Box sx={{ p: 1, mb: 1, minHeight: 200, direction: "rtl" }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>{title ?? "מדדי חשיפה משוקללים"}</Typography>
        {allPolicies.length > 0 && <SavingsSummary policies={allPolicies} />}
        {onOpenYieldLab && (
          <Button
            variant="contained"
            startIcon={<TrendingUpIcon sx={{ ml: 1 }} />}
            onClick={onOpenYieldLab}
            size="small"
          >
            פתח מעבדת תשואות
          </Button>
        )}
      </Box>
      <Box textAlign="right" mb={2}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          לחץ על הקוביות בקשת כדי לסמן את טווח היעד. קוביה שווה 10 אחוז
        </Typography>
      </Box>
      <Box textAlign="right" mb={2}>
        <Typography variant="h6">
          סך הכל: {formatCurrency(totalAmount)}
          {totalExcludedAmount > 0 && (
            <Tooltip title={buildExcludedTooltip()} arrow>
              <Box component="span" style={{ color: "gray", marginRight: 25, cursor: "pointer" }}>
                הוצא מהחישוב:
                <WarningAmberIcon color="warning" sx={{ ml: 0.5 }} />
                ({formatCurrency(totalExcludedAmount)})
              </Box>
            </Tooltip>
          )}
        </Typography>
      </Box>
      <Grid container spacing={0} justifyContent="center" dir="rtl">
        <Grid item xs={12} md={4}>
          {renderGauge(
            "stock-exposure-gauge",
            stockExposure,
            "חשיפה למניות",
            preferenceData?.stockExposure,
            handleStockExposureChange,
            4,
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderGauge(
            "currency-exposure-gauge",
            currencyExposure,
            'חשיפה למט"ח',
            preferenceData?.currencyExposure,
            handleCurrencyExposureChange,
            5,
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderGauge(
            "foreign-exposure-gauge",
            foreignExposure,
            'חשיפה לחו"ל',
            preferenceData?.foreignExposure,
            handleForeignExposureChange,
            5,
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExposureGaugesByMimshakDataInteractive;
